'use client'

import { useEffect, useState } from 'react'
import { useExtracted } from 'next-intl'
import { cn } from '@/lib/utils'
import { VideoCreditBadge } from '@/components/marketing/VideoCreditBadge'
import { BACKGROUND_VIDEOS } from '@/lib/marketing-config'

/**
 * Renders a randomly-selected blurred background video with a gradient overlay
 * and a credit badge. Used across onboarding and marketing pages.
 *
 * `badgeClassName` overrides the credit badge's position for pages whose
 * bottom strip is occupied (e.g. select-plan's pinned CTA bar).
 *
 * `hideCredit` drops the credit badge entirely — used inside onboarding, where
 * the badge is an external link that would pull the user out of the flow.
 */
export function VideoBackground({
  badgeClassName,
  hideCredit = false,
}: {
  badgeClassName?: string
  hideCredit?: boolean
}) {
  const t = useExtracted()
  const [videoIndex, setVideoIndex] = useState<number | null>(null)

  useEffect(() => {
    setVideoIndex(Math.floor(Math.random() * BACKGROUND_VIDEOS.length))
  }, [])

  const selectedVideo = videoIndex !== null ? BACKGROUND_VIDEOS[videoIndex] : null

  return (
    <>
      {/* Video Background */}
      <div className="absolute inset-0 h-full w-full bg-black">
        {selectedVideo && (
          <video
            key={selectedVideo.src}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            aria-label={t('Background video showcasing AI filmmaking')}
            className="absolute inset-0 h-full w-full scale-110 object-cover blur-md"
            style={{ objectPosition: 'center center' }}
          >
            <source src={selectedVideo.src} type="video/mp4" />
          </video>
        )}
      </div>

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/65 to-black/85" />

      {/* Video Credit Badge */}
      {selectedVideo && !hideCredit && (
        <div className={cn('absolute z-20', badgeClassName ?? 'bottom-6 end-6 md:bottom-8 md:end-8')}>
          <VideoCreditBadge video={selectedVideo} />
        </div>
      )}
    </>
  )
}
